import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import {
  ClinicalSpec,
  ClinicalSourceRecord,
  RuleClass,
  RULE_CLASS_MEANING,
  CLIENT_CONFIGURATION_REQUIRED,
  MedicationMatrixRow,
  EligibilityRule,
  GuardrailRule,
  AuthorityMatrixRow,
  REFERENCE_ROLES,
  isFirstLineRole,
  CategoryBoundTerm,
  boundsToTerms,
} from "./clinical-spec-types";
import { MasterParameter, MasterMedicationClass } from "../../../_services/ClinicalMastersService";
import { WorkflowV2 } from "./workflow-types";

/**
 * Clinical Specification section editors — the studio portlets' spec-shaped
 * surfaces. Each exported *Sections component renders one requirements area
 * (SynerGen LMS Implementation Plan §5–§13) as governed, editable reference
 * data stored in `config.clinicalSpec`. Every section shows its §-reference
 * so what is on screen traces back to the document (§18.1 rule traceability).
 */

interface SectionsProps {
  spec: ClinicalSpec;
  update: (patch: Partial<ClinicalSpec>) => void;
  /**
   * Derived client copy (basedOn set, not a reference). Class is PROVENANCE,
   * not choice: badges replace the select; inherited A is fully locked
   * (fields, class and deletion); adding or modifying anything else
   * auto-classifies it C — §17.2 Step 4's Adopt/Override made self-marking.
   */
  derived?: boolean;
}

// ── Shared primitives ──

export const SpecRef: React.FC<{ r?: string }> = ({ r }) =>
  r ? (
    <span className="cs-specref" title={`SynerGen LMS Implementation Plan2-CWaves.pdf — section ${r.replace("§", "")}`}>
      {r}
    </span>
  ) : null;

export const ClassBadge: React.FC<{ c?: RuleClass }> = ({ c }) =>
  c ? (
    <span className={`cs-class cs-class-${c.toLowerCase()}`} title={RULE_CLASS_MEANING[c]}>
      {c}
    </span>
  ) : null;

/** Editable §6 governance-class assignment — classification is a deliberate act. */
export const ClassSelect: React.FC<{ value?: RuleClass; onChange: (c: RuleClass) => void }> = ({ value, onChange }) => (
  <Form.Select
    size="sm"
    style={{ width: 58, fontWeight: 700 }}
    value={value || ""}
    title={value ? RULE_CLASS_MEANING[value] : "Assign a governance class (§6: every executable rule shall be assigned one)"}
    onChange={(e) => { if (e.target.value) onChange(e.target.value as RuleClass); }}
  >
    <option value="" disabled>—</option>
    <option value="A">A</option>
    <option value="B">B</option>
    <option value="C">C</option>
  </Form.Select>
);

/**
 * Class control with the §6 ratchet: when locked and the value is A, renders
 * an immovable badge; otherwise the select (upgrading to A is always allowed —
 * stricter is permitted, weaker is not).
 */
const DERIVED_CLASS_TITLES: Record<RuleClass, string> = {
  A: "Class A — SynerGen mandatory safety, locked in a client copy (§6: shall not be silently weakened). Content, class and deletion are managed in the reference.",
  B: "Class B — inherited SynerGen reference rule, adopted as-is. Editing it reclassifies it C (your deployment's override, per §17.2 Step 4).",
  C: "Class C — your deployment's rule (added or modified in this copy), governed by your clinical process.",
};

export const ClassControl: React.FC<{
  value?: RuleClass;
  onChange: (c: RuleClass) => void;
  derived?: boolean;
}> = ({ value, onChange, derived }) =>
  derived ? (
    value ? (
      <span
        className={`cs-class cs-class-${value.toLowerCase()}`}
        style={value === "A" ? { cursor: "not-allowed" } : undefined}
        title={DERIVED_CLASS_TITLES[value]}
      >
        {value}
      </span>
    ) : null
  ) : (
    <ClassSelect value={value} onChange={onChange} />
  );

/**
 * Collapsible wrapper for DECLARED spec content — the audited reference text
 * from the requirements document (SME-reviewed, §18.1-traceable) that the
 * machine does not execute. Portlets lead with the enforced layer (workflow
 * guards, rules, capture) and tuck this dossier underneath, collapsed, so
 * operational UI and governance record never get confused for each other.
 */
export const GovernanceRecord: React.FC<{
  specRef?: string;
  /** Accurate one-liner about this record's enforcement relationship. */
  hint: string;
  defaultOpen?: boolean;
  /** Base-side lock: content opens read-only; editing is an explicit, confirmed
   *  act (a specification revision feeding SME review + a version bump).
   *  Pass false on derived copies — their gating is the DeploymentLock /
   *  CCR-resolution flows, which must stay directly usable. */
  lockEdits?: boolean;
  children: React.ReactNode;
}> = ({ specRef, hint, defaultOpen = false, lockEdits = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const locked = lockEdits && !editing;
  return (
    <div className={"cs-govrec" + (open ? " open" : "")}>
      <button type="button" className="cs-govrec-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="cs-govrec-chevron">{open ? "▾" : "▸"}</span>
        <span className="cs-govrec-title">Governance record</span>
        <SpecRef r={specRef} />
        <span className="cs-govrec-hint">{hint}</span>
      </button>
      {open && (
        <div className="cs-govrec-body">
          {lockEdits && (locked ? (
            <div className="cs-lock-bar">
              <span className="cs-lock-msg">🔒 Read-only — this is the audited reference text; changing it is a specification revision</span>
              <Button
                type="button" size="sm" variant="secondary"
                onClick={() => {
                  if (window.confirm(
                    "Edit this governance record?\n\nThis content is what SMEs review and auditors sign. Revisions should go through SME review and a version bump before republishing.",
                  )) setEditing(true);
                }}
              >
                Edit governance record…
              </Button>
            </div>
          ) : (
            <div className="cs-lock-bar">
              <span className="cs-lock-msg">✎ Editing governance record — route changes through SME review and a version bump</span>
            </div>
          ))}
          <div className={locked ? "cs-lock-body" : undefined}>{children}</div>
        </div>
      )}
    </div>
  );
};

const CcrBadge: React.FC = () => (
  <span className="cs-ccr" title="§7 / §11.3 — this deployment value remains intentionally unresolved until a client provides and approves it. §20.3: no required CLIENT_CONFIGURATION_REQUIRED value may remain unresolved in an executable production pathway.">
    CLIENT_CONFIGURATION_REQUIRED
  </span>
);

/**
 * A CCR deployment value that is resolvable ON DERIVED COPIES (§17.2 step: the
 * client supplies it) and locked on bases (§7: bases stay intentionally
 * unresolved). Click the badge to enter the client's value; ↺ restores CCR.
 */
const CcrValue: React.FC<{
  value: string;
  derived?: boolean;
  onChange: (v: string) => void;
}> = ({ value, derived, onChange }) => {
  if (value === CLIENT_CONFIGURATION_REQUIRED) {
    return derived ? (
      <span onClick={() => onChange("")} style={{ cursor: "pointer" }} title="Click to resolve with this client's value">
        <CcrBadge />
      </span>
    ) : (
      <CcrBadge />
    );
  }
  if (!derived) return <>{value}</>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "100%" }}>
      <Form.Control
        size="sm"
        value={value}
        placeholder="client value"
        autoFocus={value === ""}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="drs-del"
        title="Revert to CLIENT_CONFIGURATION_REQUIRED"
        onClick={() => onChange(CLIENT_CONFIGURATION_REQUIRED)}
      >↺</button>
    </span>
  );
};

/**
 * §6 / §17.2 Step 4 — deployment lock for Class B reference content.
 * On a DERIVED copy, inherited SynerGen sections render read-only until the
 * author explicitly overrides them for this deployment (a deliberate, logged
 * act: the confirm states that edits reclassify content to Class C). Sections
 * already containing C content are considered overridden and stay unlocked.
 * Bases are never locked — the studio is their authoring surface.
 */
export const DeploymentLock: React.FC<{
  derived?: boolean;
  /** Whether the wrapped content already carries client (Class C) overrides. */
  overridden: boolean;
  /** Human name for the confirm dialog, e.g. "the BP classification". */
  what: string;
  children: React.ReactNode;
}> = ({ derived, overridden, what, children }) => {
  const [unlocked, setUnlocked] = useState(false);
  if (!derived) return <>{children}</>;
  const locked = !overridden && !unlocked;
  return (
    <div>
      {locked && (
        <div className="cs-lock-bar">
          <span className="cs-lock-msg">🔒 SynerGen reference content (Class B) — read-only in this deployment copy</span>
          <Button
            type="button" size="sm" variant="secondary"
            onClick={() => {
              if (window.confirm(
                `Override ${what} for this deployment?\n\nEdited content is reclassified Class C — your organization's clinical governance owns it from that point (§6, §17.2 Step 4). The SynerGen reference stays intact on the base template.`,
              )) setUnlocked(true);
            }}
          >
            Override for this deployment…
          </Button>
        </div>
      )}
      <div className={locked ? "cs-lock-body" : undefined} aria-disabled={locked || undefined}>
        {children}
      </div>
    </div>
  );
};

/**
 * ⓘ affordance with a click-open popover — for lint/derived lines whose meaning
 * isn't obvious from their text. Richer than a native title tooltip: real
 * paragraphs, stays open while reading, closes on the icon or the ×.
 */
export const InfoTip: React.FC<{ label?: string; children: React.ReactNode }> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="cs-infotip">
      <button type="button" className="cs-infotip-btn" aria-expanded={open}
        title={open ? undefined : label ?? "What does this mean?"}
        onClick={() => setOpen(!open)}>ⓘ</button>
      {open && (
        <span className="cs-infotip-pop" role="note">
          <button type="button" className="drs-del cs-infotip-close" title="Close" onClick={() => setOpen(false)}>×</button>
          {children}
        </span>
      )}
    </span>
  );
};

const Sect: React.FC<{ title: string; specRef?: string; hint?: string; children: React.ReactNode }> = ({ title, specRef, hint, children }) => (
  <div className="cs-sect">
    <div className="cs-sect-head">
      <span className="cs-sect-title">{title}</span>
      <SpecRef r={specRef} />
      {hint && <span className="cs-sect-hint">{hint}</span>}
    </div>
    {children}
  </div>
);

/** Chip-list editor for the spec's many bullet lists. */
export const ListEditor: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  ordered?: boolean;
  /** Read-only rendering (A-locked content in derived copies). */
  disabled?: boolean;
}> = ({ items, onChange, placeholder, ordered, disabled }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div className="cs-list">
      {items.map((it, i) => (
        <span key={i} className="cs-list-item">
          {ordered && <span className="cs-list-num">{i + 1}.</span>}
          {it}
          {!disabled && <button type="button" className="cs-list-del" title="Remove" onClick={() => onChange(items.filter((_, x) => x !== i))}>×</button>}
        </span>
      ))}
      {!disabled && <span className="cs-list-add">
        <Form.Control
          size="sm"
          value={draft}
          placeholder={placeholder || "add…"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button type="button" size="sm" variant="secondary" onClick={add} disabled={!draft.trim()}>+</Button>
      </span>}
    </div>
  );
};

const emptyHint = (what: string) => (
  <div className="drs-hint">No {what} configured — the SynerGen reference ships this populated; client copies inherit it.</div>
);

/**
 * Parameter-aware list editor: entries that resolve against the clinical
 * parameter vocabulary render as coded chips (◆, with LOINC tooltip);
 * anything else renders as ⚠ uncoded free text — accepted as narrative,
 * visibly not executable.
 */
const normParamText = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

/** Resolve a list entry against the clinical-parameter vocabulary (◆ coded ↔ ⚠ free text). */
export const resolveParam = (item: string, parameters?: MasterParameter[]): MasterParameter | undefined =>
  (parameters || []).find(
    (p) => p.paramKey === item
      || p.paramKey === normParamText(item)
      || p.name.toLowerCase() === item.trim().toLowerCase()
      || (!!p.abbreviation && p.abbreviation.toLowerCase() === item.trim().toLowerCase()),
  );

export const ParamListEditor: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  parameters?: MasterParameter[];
  placeholder?: string;
}> = ({ items, onChange, parameters, placeholder }) => {
  const [draft, setDraft] = useState("");
  const findParam = (item: string): MasterParameter | undefined => resolveParam(item, parameters);
  // Entries that resolve against the vocabulary are STORED as paramKeys (the
  // engine matches captured data and rule params by key); display stays the
  // vocabulary name either way. Unresolvable text is stored as typed (⚠).
  const add = (v: string) => {
    const key = findParam(v)?.paramKey ?? v;
    if (key && !items.includes(key)) onChange([...items, key]);
    setDraft("");
  };
  return (
    <div className="cs-list">
      {items.map((it, i) => {
        const p = findParam(it);
        return (
          <span
            key={i}
            className={"cs-list-item " + (p ? "cs-param-coded" : "cs-param-uncoded")}
            title={p
              ? `${p.name}${p.abbreviation ? ` (${p.abbreviation})` : ""}${p.code ? ` · ${p.codeSystem ?? "LOINC"} ${p.code}` : ""}${p.units ? ` · ${p.units}` : ""} — coded parameter (${p.dataType})`
              : "Uncoded free text — not executable. Pick from the vocabulary, or add it as a parameter in Clinical Domains first."}
          >
            {p ? "◆ " : "⚠ "}{p ? (p.abbreviation ? `${p.name} (${p.abbreviation})` : p.name) : it}
            <button type="button" className="cs-list-del" title="Remove" onClick={() => onChange(items.filter((_, x) => x !== i))}>×</button>
          </span>
        );
      })}
      {(parameters || []).length > 0 && (
        <Form.Select
          size="sm"
          style={{ width: 165, fontSize: 11.5 }}
          value=""
          title="Clinical parameter vocabulary (managed in Clinical Domains)"
          onChange={(e) => { if (e.target.value) add(e.target.value); }}
        >
          <option value="">+ parameter…</option>
          {(parameters || [])
            .filter((p) => !items.some((it) => findParam(it)?.paramKey === p.paramKey))
            .map((p) => <option key={p.paramKey} value={p.paramKey}>{p.abbreviation ? `${p.name} (${p.abbreviation})` : p.name}</option>)}
        </Form.Select>
      )}
      <span className="cs-list-add">
        <Form.Control
          size="sm"
          value={draft}
          placeholder={placeholder || "free text…"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft.trim()); } }}
        />
        <Button type="button" size="sm" variant="secondary" onClick={() => add(draft.trim())} disabled={!draft.trim()}>+</Button>
      </span>
    </div>
  );
};

/** Medication-class select over the ATC-keyed master, with ⚠ fallback for unmatched legacy names. */
const MedClassPicker: React.FC<{
  value: string;
  classKey?: string;
  medicationClasses?: MasterMedicationClass[];
  onPick: (patch: { classKey?: string; name: string }) => void;
}> = ({ value, classKey, medicationClasses, onPick }) => {
  const matched = (medicationClasses || []).find((m) => m.classKey === classKey || m.name === value);
  if (!medicationClasses || medicationClasses.length === 0) {
    return <Form.Control size="sm" value={value} onChange={(e) => onPick({ name: e.target.value })} />;
  }
  return (
    <>
      <Form.Select
        size="sm"
        value={matched?.classKey ?? ""}
        title={matched ? `${matched.name}${matched.atcCode ? ` · ATC ${matched.atcCode}` : ""}` : "Not in the medication-class master — pick to code it"}
        onChange={(e) => {
          const sel = medicationClasses.find((m) => m.classKey === e.target.value);
          if (sel) onPick({ classKey: sel.classKey, name: sel.name });
        }}
      >
        <option value="">{value ? `⚠ ${value}` : "— pick class —"}</option>
        {medicationClasses.map((m) => <option key={m.classKey} value={m.classKey}>{m.name}</option>)}
      </Form.Select>
      {matched?.atcCode && <code style={{ fontSize: 10, color: "var(--text-faint)" }}>ATC {matched.atcCode}</code>}
    </>
  );
};

/** Inline add-input for the §9.3 eligibility-factor pipeline (appends at the end — order is the pipeline order). */
const FactorAdder: React.FC<{ onAdd: (factor: string) => void }> = ({ onAdd }) => {
  const [draft, setDraft] = useState("");
  const commit = () => { if (draft.trim()) { onAdd(draft.trim()); setDraft(""); } };
  return (
    <span className="cs-list-item" style={{ background: "none", border: "none", padding: 0 }}>
      <Form.Control size="sm" style={{ width: 190, fontSize: 11.5 }} value={draft}
        placeholder="add factor…"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} />
      <Button type="button" size="sm" variant="secondary" style={{ fontSize: 11, padding: "1px 8px", marginLeft: 4 }}
        disabled={!draft.trim()} onClick={commit}>+</Button>
    </span>
  );
};

/** §9.2 reference-role select over REFERENCE_ROLES, with ⚠ fallback for legacy free text. */
const RoleSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const canonical = (REFERENCE_ROLES as readonly string[]).includes(value);
  return (
    <Form.Select
      size="sm"
      value={value}
      title={canonical || !value
        ? "Canonical role — First-line rows derive the §9.1 list"
        : "Legacy free-text role — pick a canonical role to make it executable"}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">— role —</option>
      {!canonical && value && <option value={value}>⚠ {value}</option>}
      {REFERENCE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
    </Form.Select>
  );
};

// ── Pathway Specification portlet (§8) ──

export const PathwaySpecSections: React.FC<SectionsProps & {
  /** When provided, ALL typeClassification edits route through this — the studio
   *  merges the patch and auto-syncs the bound capture fields in one write. */
  onTcChange?: (patch: Partial<NonNullable<ClinicalSpec["typeClassification"]>>) => void;
  /** Live executable workflow — the Decision Sequence map derives from its states' decisionStep tags. */
  workflowDef?: WorkflowV2 | null;
  /** Masters parameter vocabulary — feeds the classification bound-term pickers. */
  parameters?: MasterParameter[];
}> = ({ spec, update, derived, onTcChange, workflowDef, parameters }) => {
  const cls = spec.classification || {};
  // typeClassification with a shim for unmigrated legacy configs (hypertensionType)
  const legacyHt = (spec as any).hypertensionType;
  const tc: NonNullable<ClinicalSpec["typeClassification"]> = spec.typeClassification
    || (legacyHt
      ? { label: "Hypertension Type", types: legacyHt.types, suspicionIndicators: legacyHt.secondaryIndicators, confirmationRule: legacyHt.confirmationRule, specRef: legacyHt.specRef }
      : {});
  const setClassification = (patch: any) => update({ classification: { ...cls, ...patch } });
  const setTc = (patch: any) => {
    if (onTcChange) { onTcChange(patch); return; } // studio merges + auto-syncs capture options
    const reclass = derived && tc.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as RuleClass } : {};
    update({ typeClassification: { ...tc, ...patch, ...reclass } });
  };

  const classificationOverridden =
    (cls.categories || []).some((c) => c.ruleClass === "C") || cls.treatmentGoal?.ruleClass === "C";

  return (
    <div className="cs">
      <DeploymentLock derived={derived} overridden={classificationOverridden} what="the BP classification">
      <Sect title="Clinical Classification" specRef={cls.specRef || "§8.1"}
        hint={cls.system || "2025 AHA/ACC multisociety adult hypertension guideline"}>
        {(cls.categories || []).length === 0 && emptyHint("clinical classification")}
        <table className="cs-table">
          <tbody>
            {(cls.categories || []).map((c, i) => {
              const setCat = (patch: any) => {
                const reclass = derived && c.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as RuleClass } : {};
                setClassification({ categories: (cls.categories || []).map((x, y) => (y === i ? { ...x, ...patch, ...reclass } : x)) });
              };
              const b = c.bounds || {};
              const terms = boundsToTerms(b);
              const aLocked = derived && c.ruleClass === "A";
              const numericParams = (parameters || []).filter((p) => p.dataType === "numeric");
              // Writing terms drops the legacy SBP/DBP slots — clean migration on edit.
              const setTerms = (nextTerms: CategoryBoundTerm[], join?: "and" | "or") =>
                setCat({ bounds: { join: join ?? b.join ?? "and", terms: nextTerms } });
              const setTerm = (ti: number, patch: Partial<CategoryBoundTerm>) =>
                setTerms(terms.map((t, x) => (x === ti ? { ...t, ...patch } : t)));
              return (
                <tr key={c.id || i}>
                  <td className="cs-table-key" style={{ width: 180 }}>
                    <Form.Control size="sm" value={c.label} disabled={aLocked} onChange={(e) => setCat({ label: e.target.value })} />
                  </td>
                  <td>
                    <Form.Control size="sm" value={c.criteria} disabled={aLocked} onChange={(e) => setCat({ criteria: e.target.value })} />
                    <div className="cs-bounds" title="Machine-evaluable boundaries (half-open: ≥ lower, < upper), parameter-keyed against the masters vocabulary — the engine computes the category from captured data">
                      {terms.map((t, ti) => (
                        <React.Fragment key={ti}>
                          {ti > 0 && (
                            <Form.Select size="sm" style={{ width: 62 }} value={b.join || "and"} disabled={aLocked}
                              onChange={(e) => setTerms(terms, e.target.value as "and" | "or")}>
                              <option value="and">and</option>
                              <option value="or">or</option>
                            </Form.Select>
                          )}
                          {numericParams.length > 0 ? (
                            <Form.Select size="sm" style={{ width: 118 }} value={t.param} disabled={aLocked}
                              title="Masters parameter this term evaluates"
                              onChange={(e) => setTerm(ti, { param: e.target.value })}>
                              {!t.param && <option value="">— param —</option>}
                              {t.param && !numericParams.some((p) => p.paramKey === t.param) && (
                                <option value={t.param}>{t.param} ⚠</option>
                              )}
                              {numericParams.map((p) => (
                                <option key={p.paramKey} value={p.paramKey}>{p.abbreviation || p.name}</option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control size="sm" style={{ width: 110 }} value={t.param} disabled={aLocked} placeholder="param"
                              onChange={(e) => setTerm(ti, { param: e.target.value })} />
                          )}
                          <span className="cs-guardword">≥</span>
                          <Form.Control size="sm" type="number" value={t.gte ?? ""} disabled={aLocked}
                            onChange={(e) => setTerm(ti, { gte: e.target.value === "" ? undefined : Number(e.target.value) })} />
                          <span className="cs-guardword">&lt;</span>
                          <Form.Control size="sm" type="number" value={t.lt ?? ""} disabled={aLocked}
                            onChange={(e) => setTerm(ti, { lt: e.target.value === "" ? undefined : Number(e.target.value) })} />
                          {!aLocked && terms.length > 1 && (
                            <button type="button" className="drs-del" title="Remove term"
                              onClick={() => setTerms(terms.filter((_, x) => x !== ti))}>×</button>
                          )}
                        </React.Fragment>
                      ))}
                      {!aLocked && (
                        <Button type="button" size="sm" variant="secondary" style={{ fontSize: 10.5, padding: "0 7px" }}
                          onClick={() => setTerms([...terms, { param: numericParams[0]?.paramKey || "" }])}>
                          + term
                        </Button>
                      )}
                    </div>
                  </td>
                  <td style={{ width: 64, textAlign: "center" }}><ClassControl value={c.ruleClass} onChange={(rc) => setCat({ ruleClass: rc })} derived={derived} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {cls.treatmentGoal && (
          <div className="cs-goal">
            <span className="cs-goal-label">Treatment goal</span>
            <Form.Control size="sm" value={cls.treatmentGoal.text} onChange={(e) => setClassification({ treatmentGoal: { ...cls.treatmentGoal, text: e.target.value, ...(derived && cls.treatmentGoal?.ruleClass !== "A" ? { ruleClass: "C" as RuleClass } : {}) } })} />
            <ClassBadge c={cls.treatmentGoal.ruleClass} />
          </div>
        )}
        {/* §8.1 distinction rule — demoted to a hover glyph: present for auditors, invisible furniture for operators */}
        {cls.distinctionNote && (
          <span className="cs-distinction-glyph" title={cls.distinctionNote}>⚖ §8.1</span>
        )}
      </Sect>
      </DeploymentLock>

      <DeploymentLock derived={derived} overridden={tc.ruleClass === "C"} what="the type classification">
      <Sect title={tc.label || "Type Classification"} specRef={tc.specRef || "§8.3 Step 3"}
        hint="Etiologic classification with referral triggers — executed by the workflow's type-classification step">
        <div className="cs-inline-label">Section label <small>(this is data on THIS resource — it names the block above)</small></div>
        <Form.Control size="sm" style={{ maxWidth: 320, marginBottom: 6 }} value={tc.label || ""}
          placeholder='e.g. "Hypertension Type" / "HF Etiology" / "Stroke Etiology"'
          onChange={(e) => setTc({ label: e.target.value })} />
        <div className="cs-inline-label">Types <small>(the options the clinician chooses between at execution — guards fork on the answer)</small></div>
        <ListEditor items={tc.types || []} onChange={(v) => setTc({ types: v })} placeholder="e.g. Ischemic" disabled={derived && tc.ruleClass === "A"} />
        <div className="cs-inline-label">Suspicion indicators <ClassControl value={tc.ruleClass} onChange={(c) => setTc({ ruleClass: c })} derived={derived} /></div>
        <ListEditor items={tc.suspicionIndicators || []} onChange={(v) => setTc({ suspicionIndicators: v })} placeholder="e.g. Resistant hypertension" disabled={derived && tc.ruleClass === "A"} />
        {tc.confirmationRule && <div className="cs-note">🛡 {tc.confirmationRule}</div>}

        {/* Capture links — convention over configuration: the type field is the
            masters parameter `etiology_type`, indicators `etiology_indicators`.
            When the workflow follows the convention the binding is automatic and
            shown as a static check; a picker appears ONLY on mismatch (legacy
            ids, missing field), i.e. exactly when a human decision is needed. */}
        {workflowDef && (() => {
          const TYPE_CONVENTION = "etiology_type";
          const IND_CONVENTION = "etiology_indicators";
          const captureFields = workflowDef.states.flatMap((s) => s.capture ?? []);
          const fieldExists = (id?: string) => !!id && captureFields.some((c) => c.id === id);
          const effType = tc.typeCaptureFieldId || (fieldExists(TYPE_CONVENTION) ? TYPE_CONVENTION : undefined);
          const effInd = tc.captureFieldId || (fieldExists(IND_CONVENTION) ? IND_CONVENTION : undefined);
          const fieldPicker = (value: string | undefined, onPick: (v?: string) => void, label: string, hint: string) => (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span className="drs-mini-label" style={{ margin: 0 }} title={hint}>{label}</span>
              <Form.Select size="sm" style={{ width: 180, fontSize: 11.5 }} value={value || ""} onChange={(e) => onPick(e.target.value || undefined)}>
                <option value="">— not linked —</option>
                {captureFields.map((c) => <option key={c.id} value={c.id}>{c.id}</option>)}
              </Form.Select>
            </span>
          );
          const linkView = (eff: string | undefined, convention: string, onPick: (v?: string) => void, label: string, hint: string) =>
            eff === convention && fieldExists(convention) ? (
              <span className="drs-mini-label" style={{ margin: 0 }} title={`${hint} — bound by convention to the masters parameter`}>
                {label}: <code style={{ fontSize: 11 }}>{convention}</code> ✓
              </span>
            ) : (
              fieldPicker(eff, onPick, label, `${hint} (convention: ${convention})`)
            );
          const typeGuardIssues = effType
            ? workflowDef.transitions
                .filter((t) => t.guard?.type === "field" && t.guard.field === effType && ["equals", "notEquals", "in"].includes(t.guard.op))
                .flatMap((t) => {
                  const g = t.guard as { value?: unknown };
                  const vals = Array.isArray(g.value) ? g.value : [g.value];
                  return vals
                    .filter((v): v is string => typeof v === "string" && !(tc.types || []).includes(v))
                    .map((v) => ({ action: t.label, value: v }));
                })
            : [];
          return (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                {linkView(effType, TYPE_CONVENTION, (v) => setTc({ typeCaptureFieldId: v }), "Type field",
                  "The capture select recording the chosen type — sync writes the Types list as its options; guards fork on its value")}
                {linkView(effInd, IND_CONVENTION, (v) => setTc({ captureFieldId: v }), "Indicators field",
                  "The capture multiselect recording observed indicators — its options mirror the indicator list automatically")}
              </div>
              {typeGuardIssues.length > 0 && (
                <div className="drs-lint" style={{ marginTop: 8 }}>
                  <strong>⚠ Guard/type mismatch</strong>
                  <ul>
                    {typeGuardIssues.map((iss, x) => (
                      <li key={x}>
                        The action <b>"{iss.action}"</b> compares <code>{effType}</code> to
                        {" "}<b>"{iss.value}"</b>, which is not one of the Types above — the fork would never fire.
                        Rename the type back, or update the guard in Safety &amp; Guardrail Matrix.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}
      </Sect>
      </DeploymentLock>

    </div>
  );
};

/**
 * §8.3 sequence overview — a LIVE COMPANION PANEL, not in-flow content: the
 * studio renders it in a sticky rail beside the pathway portlet so it updates
 * as the machine below is edited. Each step lists the states tagged to it in
 * the builder; the map derives at render time and cannot go stale.
 */
export const DecisionSequenceMap: React.FC<{ spec: ClinicalSpec; workflowDef?: WorkflowV2 | null }> = ({ spec, workflowDef }) => {
  const seq = spec.decisionSequence || [];
  if (seq.length === 0) {
    return (
      <div className="cs-sect">
        <div className="cs-sect-head"><span className="cs-sect-title">Sequence overview</span><SpecRef r="§8.3" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          No §8.3 decision sequence declared for this resource.
        </p>
      </div>
    );
  }
  return (
    <Sect title="Sequence overview" specRef="§8.3"
      hint={workflowDef
        ? "Live view of the workflow builder — tag a state's decision step there to remap; this cannot go stale"
        : "The reference order — build the workflow to see each step mapped to its executing states"}>
      <div className="cs-seq">
        {seq.map((s) => {
          // Derive the mapping from state tags when a live machine exists;
          // stored workflowStateIds only back-fill legacy resources.
          const stateIds = workflowDef
            ? workflowDef.states.filter((st) => st.decisionStep === s.step).map((st) => st.id)
            : s.workflowStateIds || [];
          return (
            <div key={s.step} className="cs-seq-step">
              <span className="cs-seq-n">{s.step}</span>
              <span className="cs-seq-name">{s.name}</span>
              {stateIds.map((id) => {
                const label = workflowDef?.states.find((st) => st.id === id)?.label;
                return (
                  <span key={id} className="cs-seq-state">
                    {label ?? <code>{id}</code>}
                    {label && <code className="cs-seq-state-id">{id}</code>}
                  </span>
                );
              })}
              {workflowDef && stateIds.length === 0 && (
                <span className="cs-seq-unmapped">⚠ no workflow state tagged to this step</span>
              )}
            </div>
          );
        })}
        {workflowDef && (() => {
          const untagged = workflowDef.states.filter((st) => st.decisionStep === undefined);
          return untagged.length > 0 ? (
            <div className="cs-seq-untagged">
              Outside the §8.3 sequence (untagged):{" "}
              {untagged.map((st) => (
                <span key={st.id} className="cs-seq-state">
                  {st.label}
                  <code className="cs-seq-state-id">{st.id}</code>
                </span>
              ))}
            </div>
          ) : null;
        })()}
      </div>
    </Sect>
  );
};

// ── Medication Decision Matrix portlet (§9) ──

const ELIG_OPS: Array<EligibilityRule["op"]> = ["equals", "notEquals", "gt", "gte", "lt", "lte", "in", "notEmpty"];
const ELIG_ACTIONS: Array<EligibilityRule["action"]> = ["exclude", "require-review", "caution"];

const parseRuleValue = (raw: string, op: EligibilityRule["op"]): EligibilityRule["value"] => {
  if (op === "in") return raw.split(",").map((v) => v.trim()).filter(Boolean);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return raw !== "" && !isNaN(Number(raw)) ? Number(raw) : raw;
};

export const MedicationMatrixSections: React.FC<SectionsProps & {
  parameters?: MasterParameter[];
  medicationClasses?: MasterMedicationClass[];
}> = ({ spec, update, parameters, medicationClasses, derived }) => {
  const [expandedRules, setExpandedRules] = useState<number | null>(null);
  const mm = spec.medicationMatrix || {};
  const rows = mm.rows || [];
  /**
   * Single write path for the §9 matrix, with two self-healing passes:
   * key-evaluation entries that resolve against the vocabulary are conformed
   * to their paramKeys (so the execution engine can match them), and §9.1
   * firstLineClasses is DERIVED from the rows carrying the First-line role —
   * the matrix is the single source of truth, the list a rendered view.
   */
  const setMm = (patch: any) => {
    const next = { ...mm, ...patch };
    const nextRows: MedicationMatrixRow[] = next.rows || [];
    if (nextRows.length > 0) {
      next.rows = nextRows.map((r) => ({
        ...r,
        keyEvaluation: (r.keyEvaluation || []).map((k) => resolveParam(k, parameters)?.paramKey ?? k),
      }));
      const firstLine = Array.from(new Set(
        next.rows.filter((r: MedicationMatrixRow) => isFirstLineRole(r.referenceRole) && r.medicationClass).map((r: MedicationMatrixRow) => r.medicationClass),
      ));
      if (firstLine.length > 0) next.firstLineClasses = firstLine;
    }
    update({ medicationMatrix: next });
  };
  const setRow = (i: number, patch: Partial<MedicationMatrixRow>) => {
    const reclass = derived && rows[i]?.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as RuleClass } : {};
    setMm({ rows: rows.map((r, x) => (x === i ? { ...r, ...patch, ...reclass } : r)) });
  };
  const setRule = (i: number, ri: number, patch: Partial<EligibilityRule>) => {
    const target = rows[i]?.rules?.[ri];
    const reclass = derived && target?.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as RuleClass } : {};
    setRow(i, { rules: (rows[i].rules || []).map((r, x) => (x === ri ? { ...r, ...patch, ...reclass } : r)) });
  };

  // §9.1 derived view: the matrix rows with the First-line role. Non-empty
  // means derivation is active (the list self-maintains via setMm); empty
  // means a legacy resource whose roles aren't set yet — hand-editing stays
  // available until they are.
  const firstLineFromRows = Array.from(new Set(
    rows.filter((r) => isFirstLineRole(r.referenceRole) && r.medicationClass).map((r) => r.medicationClass),
  ));

  // Non-blocking authoring lint: a ◆ coded key-evaluation parameter with no
  // eligibility rule surfaces at execution only as an "assess" reminder — it
  // can never exclude or flag.
  const unruledKeyEvals = rows
    .map((r) => ({
      row: r,
      params: (r.keyEvaluation || [])
        .map((k) => resolveParam(k, parameters))
        .filter((p): p is MasterParameter => !!p)
        .filter((p) => !(r.rules || []).some((rule) => rule.param === p.paramKey)),
    }))
    .filter((x) => x.params.length > 0);

  return (
    <div className="cs">
      <Sect title="Reference First-Line Classes" specRef={"§9.1"}
        hint={firstLineFromRows.length > 0
          ? "Derived from the matrix rows carrying the First-line role — edit the roles below to change this list"
          : "No matrix row carries the First-line role yet — this list is hand-maintained until roles are set"}>
        {firstLineFromRows.length > 0 ? (
          <div className="cs-static-chips">{firstLineFromRows.map((c) => <span key={c} className="drs-chip">{c}</span>)}</div>
        ) : (
          <ListEditor items={mm.firstLineClasses || []} onChange={(v) => setMm({ firstLineClasses: v })} placeholder="medication class…" />
        )}
        {/* §9.1 formulary-distinction rule — hover glyph: these are guideline roles;
            a formulary is the client's own approved list, supplied at deployment */}
        {mm.notAFormularyNote && (
          <span className="cs-distinction-glyph"
            title={`${mm.notAFormularyNote} (§9.1 — first-line status is the guideline's clinical recommendation; which drugs an organization approves/stocks is its formulary, a client deployment input resolved in Client Formulary & Preferences.)`}>
            ⚖ §9.1
          </span>
        )}
      </Sect>

      <Sect title="Reference Matrix" specRef={mm.specRef || "§9.2"}
        hint="Medication class × reference role × key evaluation before eligibility">
        {rows.length === 0 && emptyHint("medication matrix")}
        {rows.length > 0 && (
          <div className="drs-matrix-wrap">
            <table className="cs-table cs-table-bordered cs-table-fixed">
              <thead>
                <tr><th style={{ width: 232 }}>Medication class</th><th style={{ width: 172 }}>Reference role</th><th>Key evaluation</th><th style={{ width: 84 }}>Rules</th><th style={{ width: 60 }}>Class</th><th style={{ width: 30 }}></th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <React.Fragment key={r.id || i}>
                  <tr>
                    <td>
                      <MedClassPicker
                        value={r.medicationClass}
                        classKey={r.classKey}
                        medicationClasses={medicationClasses}
                        onPick={(p) => setRow(i, { medicationClass: p.name, classKey: p.classKey })}
                      />
                    </td>
                    <td><RoleSelect value={r.referenceRole} onChange={(v) => setRow(i, { referenceRole: v })} /></td>
                    <td><ParamListEditor items={r.keyEvaluation} onChange={(v) => setRow(i, { keyEvaluation: v })} parameters={parameters} placeholder="evaluate…" /></td>
                    <td style={{ textAlign: "center" }}>
                      <Button
                        type="button" size="sm"
                        variant={(r.rules || []).length > 0 ? "primary" : "secondary"}
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        title="Structured eligibility rules — engine-evaluable per patient (the exclusion rationale of DeciiQ step 5)"
                        onClick={() => setExpandedRules(expandedRules === i ? null : i)}
                      >
                        ⚙ {(r.rules || []).length}
                      </Button>
                    </td>
                    <td style={{ textAlign: "center" }}><ClassControl value={r.ruleClass} onChange={(c) => setRow(i, { ruleClass: c })} derived={derived} /></td>
                    <td><button type="button" className="drs-del" title="Remove row" onClick={() => setMm({ rows: rows.filter((_, x) => x !== i) })}>×</button></td>
                  </tr>
                  {expandedRules === i && (
                    <tr>
                      <td colSpan={6} style={{ background: "var(--surface-page)" }}>
                        <div className="cs-pathway" style={{ margin: "4px 0" }}>
                          <div className="cs-pathway-name">
                            Eligibility rules — {r.medicationClass || r.id}
                            <span className="cs-sect-hint" style={{ fontWeight: 400 }}>guard-shaped predicates the eligibility engine evaluates per patient; the failing rule becomes the exclusion rationale</span>
                            <button type="button" className="drs-del" style={{ marginLeft: "auto" }} title="Close" onClick={() => setExpandedRules(null)}>×</button>
                          </div>
                          {(r.rules || []).map((rule, ri) => {
                            const p = (parameters || []).find((x) => x.paramKey === rule.param);
                            return (
                              <div key={rule.id || ri} className="cs-rule-row">
                                <Form.Select size="sm" style={{ width: 130 }} value={rule.action} disabled={derived && rule.ruleClass === "A"}
                                  onChange={(e) => setRule(i, ri, { action: e.target.value as EligibilityRule["action"] })}>
                                  {ELIG_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                </Form.Select>
                                <span className="cs-guardword">when</span>
                                {(parameters || []).length > 0 ? (
                                  <Form.Select size="sm" style={{ width: 190 }} value={rule.param} disabled={derived && rule.ruleClass === "A"}
                                    title={p ? `${p.name}${p.code ? ` · ${p.codeSystem ?? "LOINC"} ${p.code}` : ""}` : "Pick a coded parameter"}
                                    onChange={(e) => setRule(i, ri, { param: e.target.value })}>
                                    <option value="">— parameter —</option>
                                    {(parameters || []).map((x) => <option key={x.paramKey} value={x.paramKey}>{x.name}</option>)}
                                  </Form.Select>
                                ) : (
                                  <Form.Control size="sm" style={{ width: 160 }} value={rule.param} placeholder="parameter" disabled={derived && rule.ruleClass === "A"} onChange={(e) => setRule(i, ri, { param: e.target.value })} />
                                )}
                                <Form.Select size="sm" style={{ width: 100 }} value={rule.op} disabled={derived && rule.ruleClass === "A"}
                                  onChange={(e) => setRule(i, ri, { op: e.target.value as EligibilityRule["op"] })}>
                                  {ELIG_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                                </Form.Select>
                                {rule.op !== "notEmpty" && (
                                  <Form.Control size="sm" style={{ width: 110 }} disabled={derived && rule.ruleClass === "A"}
                                    value={Array.isArray(rule.value) ? rule.value.join(", ") : String(rule.value ?? "")}
                                    placeholder={rule.op === "in" ? "a, b, c" : p?.units ? `value (${p.units})` : "value"}
                                    onChange={(e) => setRule(i, ri, { value: parseRuleValue(e.target.value, rule.op) })}
                                  />
                                )}
                                <ClassControl value={rule.ruleClass} onChange={(c) => setRule(i, ri, { ruleClass: c })} derived={derived} />
                                <Form.Select size="sm" style={{ width: 200, fontSize: 11 }} value={rule.implementsGuardrail || ""}
                                  disabled={derived && rule.ruleClass === "A"}
                                  title="§18.1 — declare which §10.2 guardrail claim this rule implements; the guardrail table derives its Enforced-by from these declarations"
                                  onChange={(e) => setRule(i, ri, { implementsGuardrail: e.target.value || undefined })}>
                                  <option value="">implements no guardrail</option>
                                  {(spec.guardrails?.rules ?? []).map((gr) => (
                                    <option key={gr.id} value={gr.id}>⛨ {gr.trigger.length > 34 ? gr.trigger.slice(0, 34) + "…" : gr.trigger}</option>
                                  ))}
                                </Form.Select>
                                <button type="button" className="drs-del"
                                  disabled={derived && rule.ruleClass === "A"}
                                  style={derived && rule.ruleClass === "A" ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
                                  title={derived && rule.ruleClass === "A" ? "Class A rule — locked in a client copy (§6)" : "Remove rule"}
                                  onClick={() => setRow(i, { rules: (r.rules || []).filter((_, x) => x !== ri) })}>×</button>
                              </div>
                            );
                          })}
                          <Button type="button" size="sm" variant="secondary" style={{ marginTop: 6 }}
                            onClick={() => setRow(i, {
                              rules: [...(r.rules || []), {
                                id: `er_${r.id}_${(r.rules || []).length + 1}`,
                                param: "", op: "gt" as const, action: "require-review" as const, ruleClass: (derived ? "C" : "B") as RuleClass,
                              }],
                            })}>
                            + Add rule
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Button type="button" size="sm" variant="secondary" onClick={() => setMm({ rows: [...rows, { id: `mm_${rows.length + 1}`, medicationClass: "", referenceRole: "", keyEvaluation: [], ruleClass: (derived ? "C" : "B") as RuleClass }] })}>
          + Add medication class
        </Button>
        {unruledKeyEvals.length > 0 && (
          <div className="cs-lint">
            ◆ Key evaluation without an enforcing rule:{" "}
            {unruledKeyEvals.map(({ row, params }) => `${row.medicationClass || row.id}: ${params.map((p) => p.abbreviation || p.name).join(", ")}`).join(" · ")}
            {" "}
            <InfoTip label="What does this mean, and how do I clear an item?">
              <p><b>These checks are machine-readable but not machine-enforced.</b> A ◆ parameter
              on a row's Key Evaluation list is only verified automatically when that row also
              carries a structured rule (the ⚙ editor) referencing it. The items listed here have
              no such rule — at execution the pharmacist sees them as <i>"assess before
              prescribing"</i> reminders, never as automatic eligible/excluded verdicts.</p>
              <p><b>To clear an item, decide one of two things:</b><br />
              1. <b>Give it teeth</b> — open the row's ⚙ editor and add a rule for the parameter
              (e.g. thiazide: K+ &lt; 3.5 → caution). It leaves this list and becomes an automatic check.<br />
              2. <b>Leave it to judgment</b> — or remove the chip if the check doesn't belong at all.</p>
              <p style={{ marginBottom: 0 }}>The reference ships rules only where the guideline states a crisp
              threshold; the rest are deliberate SME decisions — which is why this line is a hint,
              not a publication blocker.</p>
            </InfoTip>
          </div>
        )}
        {mm.dosingBoundary && <div className="cs-note">🛡 {mm.dosingBoundary}</div>}
      </Sect>

      <Sect title="Treatment Strategy" specRef="§9.3">
        <div className="cs-grid-2">
          <div>
            <div className="cs-inline-label">Stage 1 hypertension <ClassBadge c="B" /></div>
            <Form.Control as="textarea" rows={4} style={{ fontSize: 12.5, lineHeight: 1.55 }} value={mm.treatmentStrategy?.stage1 || ""} onChange={(e) => setMm({ treatmentStrategy: { ...(mm.treatmentStrategy || { stage1: "", stage2: "" }), stage1: e.target.value } })} />
          </div>
          <div>
            <div className="cs-inline-label">Stage 2 hypertension <ClassBadge c="B" /></div>
            <Form.Control as="textarea" rows={4} style={{ fontSize: 12.5, lineHeight: 1.55 }} value={mm.treatmentStrategy?.stage2 || ""} onChange={(e) => setMm({ treatmentStrategy: { ...(mm.treatmentStrategy || { stage1: "", stage2: "" }), stage2: e.target.value } })} />
          </div>
        </div>
      </Sect>

      <GovernanceRecord specRef="§9.3" lockEdits={!derived}
        hint="The ten-factor eligibility pipeline — an audited ordered declaration, not executed as a list. Each factor's enforcement lives in its own machinery: labs/contraindications → rules, interactions → guardrails, authority → role guards, formulary/access/preference → client configuration; comorbidities, previous therapy and adherence are not yet captured.">
        <div className="cs">
          <div className="cs-inline-label">Eligibility factors applied before determining options <SpecRef r="§9.3" /></div>
          <div className="cs-factor-list">
            {(mm.eligibilityFactors || []).map((f, i) => (
              <span key={i} className="cs-list-item">
                <span className="cs-list-num">{i + 1}.</span>{f.factor}{" "}
                <ClassControl value={f.ruleClass} derived={derived}
                  onChange={(c) => setMm({ eligibilityFactors: (mm.eligibilityFactors || []).map((x, y) => (y === i ? { ...x, ruleClass: c } : x)) })} />
                <button type="button" className="cs-list-del" onClick={() => setMm({ eligibilityFactors: (mm.eligibilityFactors || []).filter((_, x) => x !== i) })}>×</button>
              </span>
            ))}
            <FactorAdder onAdd={(factor) => setMm({
              eligibilityFactors: [...(mm.eligibilityFactors || []), { factor, ruleClass: (derived ? "C" : "B") as RuleClass }],
            })} />
          </div>
        </div>
      </GovernanceRecord>
    </div>
  );
};

// ── Safety & Guardrail Matrix portlet (§10) ──

export const GuardrailSpecSections: React.FC<SectionsProps & {
  /** The machine's transitions — the §10.2 Enforced-by display DERIVES from their implementsGuardrail declarations. */
  workflowTransitions?: WorkflowV2["transitions"];
  parameters?: MasterParameter[];
  medicationClasses?: MasterMedicationClass[];
}> = ({ spec, update, workflowTransitions, parameters, medicationClasses, derived }) => {
  const g = spec.guardrails || {};
  const rules = g.rules || [];
  const setG = (patch: any) => update({ guardrails: { ...g, ...patch } });
  const setRule = (i: number, patch: Partial<GuardrailRule>) => {
    const reclass = derived && rules[i]?.ruleClass !== "A" && !("ruleClass" in patch) ? { ruleClass: "C" as RuleClass } : {};
    setG({ rules: rules.map((r, x) => (x === i ? { ...r, ...patch, ...reclass } : r)) });
  };

  return (
    <div className="cs">
      {(g.precedence || []).length > 0 && (
        <Sect title="Guardrail Precedence" specRef="§10.1" hint="Execution order — AI shall not override a hard safety rule">
          <div className="cs-seq">
            {g.precedence!.map((p, i) => (
              <div key={i} className="cs-seq-step"><span className="cs-seq-n">{i + 1}</span><span className="cs-seq-name">{p}</span></div>
            ))}
          </div>
          {g.aiOverrideRule && <div className="cs-note">🛡 {g.aiOverrideRule}</div>}
          <div style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 6 }}>
            View-only by design — precedence is the §10.1 constitution the machine's structure embodies;
            revising it is a specification change (SME review + version bump), not a configuration edit.
          </div>
        </Sect>
      )}

      <Sect title="Reference Guardrails" specRef={g.specRef || "§10.2"}
        hint="Trigger → reference behavior. 'Enforced by' records which machinery fulfills each claim — a transition guard, an eligibility rule, the rules engine, or role guards — or that it is declared only.">
        {rules.length === 0 && emptyHint("reference guardrails")}
        {rules.length > 0 && (
          <div className="drs-matrix-wrap">
            <table className="cs-table cs-table-bordered cs-table-fixed">
              <thead>
                <tr><th>Trigger</th><th>Reference behavior</th><th style={{ width: 60 }}>Class</th><th style={{ width: 220 }}>Enforced by</th><th style={{ width: 30 }}></th></tr>
              </thead>
              <tbody>
                {rules.map((r, i) => (
                  <tr key={r.id || i}>
                    <td><Form.Control size="sm" value={r.trigger} disabled={derived && r.ruleClass === "A"} onChange={(e) => setRule(i, { trigger: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={r.referenceBehavior} disabled={derived && r.ruleClass === "A"} onChange={(e) => setRule(i, { referenceBehavior: e.target.value })} /></td>
                    <td style={{ textAlign: "center" }}><ClassControl value={r.ruleClass} onChange={(c) => setRule(i, { ruleClass: c })} derived={derived} /></td>
    <td>
                      {(() => {
                        // DERIVED, read-only: enforcement is declared ON the machinery
                        // (transition/rule implementsGuardrail), never asserted here —
                        // this cell cannot be edited into a false claim.
                        const tEnf = (workflowTransitions ?? []).filter((t) => t.implementsGuardrail === r.id);
                        const rEnf = (spec.medicationMatrix?.rows ?? []).flatMap((row) =>
                          (row.rules ?? []).filter((er) => er.implementsGuardrail === r.id)
                            .map((er) => ({ er, cls: row.medicationClass })));
                        if (tEnf.length + rEnf.length > 0) {
                          return (
                            <span className="cs-enf-chips">
                              {tEnf.map((t, x) => (
                                <span key={`t${x}`} className="cs-enf-chip cs-enf-transition"
                                  title={`Workflow guard — "${t.label}" (declared on the transition in the builder)`}>
                                  ⛓ {t.id || t.label}
                                </span>
                              ))}
                              {rEnf.map(({ er, cls }, x) => (
                                <span key={`r${x}`} className="cs-enf-chip cs-enf-rule"
                                  title={`Eligibility rule — ${cls}: ${er.param} ${er.op}${er.value !== undefined ? ` ${er.value}` : ""} → ${er.action} (declared on the rule in the matrix)`}>
                                  ⚙ {er.param} {er.op}{er.value !== undefined ? ` ${er.value}` : ""}
                                </span>
                              ))}
                            </span>
                          );
                        }
                        return (
                          <span style={{ display: "block" }}>
                            <span className={r.deferral ? "cs-enf-deferred" : "cs-enf-none"}
                              title={r.deferral
                                ? `Deferred: ${r.deferral}`
                                : "No machinery declares this claim, and no deferral is recorded"}>
                              {r.deferral ? "↷ deferred" : "— unenforced —"}
                            </span>
                            <Form.Control size="sm" style={{ marginTop: 4, fontSize: 11 }}
                              value={r.deferral || ""}
                              placeholder="deferral — why & where fulfilled (e.g. EHR allergy interface at deployment — §16.1)"
                              disabled={derived && r.ruleClass === "A"}
                              onChange={(e) => setRule(i, { deferral: e.target.value || undefined })} />
                          </span>
                        );
                      })()}
                    </td>
                    <td><button type="button" className="drs-del"
                      disabled={derived && r.ruleClass === "A"}
                      style={derived && r.ruleClass === "A" ? { opacity: 0.3, cursor: "not-allowed" } : undefined}
                      title={derived && r.ruleClass === "A" ? "Class A guardrail — locked in a client copy (§6)" : "Remove guardrail"}
                      onClick={() => setG({ rules: rules.filter((_, x) => x !== i) })}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rules.length > 0 && (() => {
          const isLinked = (r: GuardrailRule) =>
            (workflowTransitions ?? []).some((t) => t.implementsGuardrail === r.id) ||
            (spec.medicationMatrix?.rows ?? []).some((row) => (row.rules ?? []).some((er) => er.implementsGuardrail === r.id));
          const linked = rules.filter(isLinked);
          const deferred = rules.filter((r) => !isLinked(r) && !!r.deferral);
          const naked = rules.filter((r) => !isLinked(r) && !r.deferral);
          const nakedA = naked.filter((r) => r.ruleClass === "A");
          return (
            <div style={{ fontSize: 11, color: "var(--text-faint)", margin: "6px 0 8px" }}>
              Enforcement (derived from machinery declarations): <b>{linked.length} linked</b> · <b>{deferred.length} deferred</b> · <b>{naked.length} unaccounted</b> of {rules.length}.
              {nakedA.length > 0 && (
                <span style={{ color: "var(--status-danger, #c53030)", fontWeight: 600 }}>
                  {" "}⚠ {nakedA.length} Class A row{nakedA.length === 1 ? "" : "s"} neither linked nor deferred — the publication gate will not pass.
                </span>
              )}
            </div>
          );
        })()}
        <Button type="button" size="sm" variant="secondary" onClick={() => setG({ rules: [...rules, { id: `gr_${rules.length + 1}`, trigger: "", referenceBehavior: "", ruleClass: (derived ? "C" : "A") as RuleClass }] })}>
          + Add guardrail
        </Button>
      </Sect>

      <Sect title="Medication-Class Guardrails" specRef="§10.3" hint="Class-specific safety checks in the reference configuration">
        {(g.medicationClassChecks || []).length === 0 && emptyHint("medication-class checks")}
        {(g.medicationClassChecks || []).map((mc, i) => {
          const master = (medicationClasses || []).find((m) => m.name.toLowerCase() === mc.medicationClass.toLowerCase().replace(/s$/, "").toLowerCase() || m.name.toLowerCase() === mc.medicationClass.toLowerCase());
          return (
            <div key={i} className="cs-pathway">
              <div className="cs-pathway-name">
                {mc.medicationClass}
                {master?.atcCode
                  ? <code style={{ fontSize: 10, color: "var(--text-faint)", fontWeight: 400 }}>ATC {master.atcCode}</code>
                  : (medicationClasses || []).length > 0 && <span className="cs-param-uncoded" style={{ fontSize: 10, fontWeight: 400 }} title="Class name does not match the medication-class master — align it so §9.2 and §10.3 join.">⚠ uncoded</span>}
              </div>
              <ParamListEditor
                items={mc.evaluate}
                onChange={(v) => setG({ medicationClassChecks: (g.medicationClassChecks || []).map((x, y) => (y === i ? { ...x, evaluate: v } : x)) })}
                parameters={parameters}
                placeholder="evaluate…"
              />
              {(() => {
                // Drift lint: §10.3 and the §9.2 Key Evaluation cover the same clinical
                // ground from two PDF sections — two hand-maintained lists that can
                // diverge. Compare per class (ATC-joined), normalized to paramKeys.
                const matrixRow = (spec.medicationMatrix?.rows ?? []).find((r) =>
                  (master && r.classKey && r.classKey === master.classKey) ||
                  r.medicationClass.toLowerCase() === mc.medicationClass.toLowerCase().replace(/s$/, ""));
                if (!matrixRow) return null;
                const norm = (s: string) => resolveParam(s, parameters)?.paramKey ?? s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
                const here = new Set(mc.evaluate.map(norm));
                const there = new Set((matrixRow.keyEvaluation || []).map(norm));
                const onlyHere = mc.evaluate.filter((s) => !there.has(norm(s)));
                const onlyThere = (matrixRow.keyEvaluation || []).filter((s) => !here.has(norm(s)));
                if (onlyHere.length === 0 && onlyThere.length === 0) return null;
                return (
                  <div className="cs-lint" style={{ marginTop: 6 }}>
                    ⚠ Diverges from the §9.2 matrix row:
                    {onlyHere.length > 0 && <> only here — {onlyHere.join(", ")};</>}
                    {onlyThere.length > 0 && <> only in the matrix — {onlyThere.join(", ")}</>}
                    <InfoTip label="Why compare these two lists?">
                      <p><b>The requirements PDF covers this content twice</b> — §9.2's "Key Evaluation"
                      (eligibility lens) and §10.3's per-class safety checks (safety lens) — so the studio
                      carries both verbatim for section-level traceability. Two hand-maintained near-copies
                      drift; this lint makes divergence visible per class (joined on the ATC master,
                      items normalized to the parameter vocabulary).</p>
                      <p style={{ marginBottom: 0 }}>Divergence isn't automatically wrong — §10.3 legitimately names hazards
                      §9.2 omits (e.g. renal artery disease) — but every difference should be a decision,
                      not an accident. Align the lists or accept the difference knowingly.</p>
                    </InfoTip>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </Sect>
    </div>
  );
};

// ── Authority & Escalation Matrix portlet (§11) ──

export const AuthoritySpecSections: React.FC<SectionsProps> = ({ spec, update }) => {
  const a = spec.authority || {};
  const matrix = a.matrix || [];
  const setA = (patch: any) => update({ authority: { ...a, ...patch } });
  const setRow = (i: number, patch: Partial<AuthorityMatrixRow>) =>
    setA({ matrix: matrix.map((r, x) => (x === i ? { ...r, ...patch } : r)) });

  return (
    <div className="cs">
      {a.principle && (
        <div className="cs-note" style={{ marginBottom: 10 }}>⚖ {a.principle} <SpecRef r="§11.1" /></div>
      )}
      {a.demoDefault && (
        <div className="cs-demo-banner" title="§7 — until client authority is resolved, unresolved execution rules default to this behavior.">
          {a.demoDefault}
        </div>
      )}

      <Sect title="Reference Matrix" specRef={a.specRef || "§11.2"}
        hint="Clinical evidence determines WHEN review is indicated; deployment configuration determines WHO acts">
        {matrix.length === 0 && emptyHint("authority matrix")}
        {matrix.length > 0 && (
          <div className="drs-matrix-wrap">
            <table className="cs-table cs-table-bordered">
              <thead>
                <tr><th>Trigger</th><th>Reference LMS behavior</th><th>Deployment-specific configuration</th><th style={{ width: 220 }}>Deployment value</th><th style={{ width: 30 }}></th></tr>
              </thead>
              <tbody>
                {matrix.map((r, i) => (
                  <tr key={r.id || i}>
                    <td><Form.Control size="sm" value={r.trigger} onChange={(e) => setRow(i, { trigger: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={r.referenceBehavior} onChange={(e) => setRow(i, { referenceBehavior: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={r.deploymentConfiguration} onChange={(e) => setRow(i, { deploymentConfiguration: e.target.value })} /></td>
                    <td>
                      {(r.deploymentValue ?? CLIENT_CONFIGURATION_REQUIRED) === CLIENT_CONFIGURATION_REQUIRED ? (
                        <span onClick={() => setRow(i, { deploymentValue: "" })} style={{ cursor: "pointer" }} title="Click to resolve with a client value"><CcrBadge /></span>
                      ) : (
                        <Form.Control size="sm" value={r.deploymentValue} placeholder="client value" autoFocus={r.deploymentValue === ""}
                          onChange={(e) => setRow(i, { deploymentValue: e.target.value })}
                          onBlur={(e) => { if (!e.target.value.trim()) setRow(i, { deploymentValue: CLIENT_CONFIGURATION_REQUIRED }); }} />
                      )}
                    </td>
                    <td><button type="button" className="drs-del" onClick={() => setA({ matrix: matrix.filter((_, x) => x !== i) })}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sect>

      <Sect title="Authority Scopes — reference state before client deployment" specRef="§11.3"
        hint="Until client authority is known, every scope stays unresolved">
        <table className="cs-table">
          <tbody>
            {(a.scopes || []).map((s, i) => (
              <tr key={i}>
                <td className="cs-table-key">{s.name}</td>
                <td>
                  {s.value === CLIENT_CONFIGURATION_REQUIRED ? (
                    <span onClick={() => setA({ scopes: (a.scopes || []).map((x, y) => (y === i ? { ...x, value: "" } : x)) })} style={{ cursor: "pointer" }} title="Click to resolve with a client value"><CcrBadge /></span>
                  ) : (
                    <Form.Control size="sm" value={s.value} placeholder="client value" autoFocus={s.value === ""}
                      onChange={(e) => setA({ scopes: (a.scopes || []).map((x, y) => (y === i ? { ...x, value: e.target.value } : x)) })}
                      onBlur={(e) => { if (!e.target.value.trim()) setA({ scopes: (a.scopes || []).map((x, y) => (y === i ? { ...x, value: CLIENT_CONFIGURATION_REQUIRED } : x)) }); }} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Sect>
    </div>
  );
};

// ── Monitoring & Outcome Specification portlet (§12) ──

export const MonitoringSpecSections: React.FC<SectionsProps & { parameters?: MasterParameter[] }> = ({ spec, update, parameters }) => {
  const m = spec.monitoring || {};
  const setM = (patch: any) => update({ monitoring: { ...m, ...patch } });
  const oc = m.outcomeCapture || { clinical: [], medication: [], decision: [], workflow: [] };

  return (
    <div className="cs">
      <Sect title="Baseline Evidence" specRef="§12.1" hint="Evaluated by the reference Decision Resource as clinically applicable — ◆ coded entries can drive the evidence-sufficiency check">
        <ParamListEditor items={m.baselineEvidence || []} onChange={(v) => setM({ baselineEvidence: v })} parameters={parameters} placeholder="evidence element…" />
      </Sect>

      <Sect title="Medication Monitoring" specRef="§12.2">
        <ParamListEditor items={m.medicationMonitoring?.items || []} onChange={(v) => setM({ medicationMonitoring: { ...(m.medicationMonitoring || { items: [] }), items: v } })} parameters={parameters} placeholder="monitor…" />
        {m.medicationMonitoring?.timingNote && <div className="cs-note">⏱ {m.medicationMonitoring.timingNote}</div>}
      </Sect>

      <Sect title="Follow-Up" specRef="§12.3">
        {m.followUp?.rule && (
          <div className="cs-goal">
            <span className="cs-goal-label">Reference rule <ClassBadge c="B" /></span>
            <Form.Control size="sm" value={m.followUp.rule} onChange={(e) => setM({ followUp: { ...(m.followUp || { evaluate: [] }), rule: e.target.value } })} />
          </div>
        )}
        <div className="cs-inline-label">Follow-up should evaluate</div>
        <ParamListEditor items={m.followUp?.evaluate || []} onChange={(v) => setM({ followUp: { ...(m.followUp || { rule: "" }), evaluate: v } })} parameters={parameters} placeholder="evaluate…" />
      </Sect>

      <Sect title="Outcome Capture" specRef="§12.4" hint="What the LMS shall capture — grouped exactly as the specification groups it">
        <div className="cs-grid-2">
          {([["clinical", "Clinical outcomes"], ["medication", "Medication outcomes"], ["decision", "Decision outcomes"], ["workflow", "Workflow outcomes"]] as const).map(([key, label]) => (
            <div key={key}>
              <div className="cs-inline-label">{label}</div>
              <ListEditor items={(oc as any)[key] || []} onChange={(v) => setM({ outcomeCapture: { ...oc, [key]: v } })} placeholder="outcome…" />
            </div>
          ))}
        </div>
      </Sect>
    </div>
  );
};

/** "add from registry…" dropdown that appends a master source's name to a string list. */
const RegistryPicker: React.FC<{
  registry?: ClinicalSourceRecord[];
  existing: string[];
  onPick: (name: string) => void;
}> = ({ registry, existing, onPick }) => {
  const candidates = (registry || []).filter((s) => !existing.includes(s.name));
  if (candidates.length === 0) return null;
  return (
    <Form.Select
      size="sm"
      style={{ width: 210, fontSize: 11.5 }}
      value=""
      title="Clinical Source master registry (managed in Clinical Domains)"
      onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
    >
      <option value="">+ add from registry…</option>
      {candidates.map((s) => (
        <option key={s.id} value={s.name}>{s.approvalStatus === "approved" ? "✓ " : "◌ "}{s.name}</option>
      ))}
    </Form.Select>
  );
};

// ── AI Workspace portlet additions (§13.2–§13.4) ──

export const AiWorkspaceSpecSections: React.FC<SectionsProps & { sourceRegistry?: ClinicalSourceRecord[] }> = ({ spec, update, sourceRegistry }) => {
  const ai = spec.aiWorkspace || {};
  const setAi = (patch: any) => update({ aiWorkspace: { ...ai, ...patch } });

  return (
    <div className="cs">
      {ai.rubric && (
        <Sect title={`Rubric — ${ai.rubric.name}`} specRef="§13.2" hint="Attach the actual rubric via the resource's Grading Rubric field; these are the reference levels">
          <table className="cs-table" style={{ maxWidth: 360 }}>
            <tbody>
              {ai.rubric.levels.map((l, i) => (
                <tr key={i}><td className="cs-table-key">{l.level}</td><td style={{ width: 70, textAlign: "right", fontWeight: 700 }}>{l.score}</td></tr>
              ))}
            </tbody>
          </table>
        </Sect>
      )}

      {ai.workspace && (
        <Sect title={`AI Workspace — ${ai.workspace.name}`} specRef="§13.2">
          <div className="cs-static-chips">{ai.workspace.settings.map((s) => <span key={s} className="drs-chip">{s}</span>)}</div>
        </Sect>
      )}

      <Sect title="RAG Sources" specRef="§13.3" hint="Reference sources RAGified by SynerGen; client deployment adds its own">
        <div className="cs-grid-2">
          <div>
            <div className="cs-inline-label">SynerGen reference sources</div>
            <ListEditor items={ai.ragSources?.reference || []} onChange={(v) => setAi({ ragSources: { ...(ai.ragSources || { clientAdds: [] }), reference: v } })} placeholder="source…" />
            <div style={{ marginTop: 5 }}>
              <RegistryPicker
                registry={sourceRegistry}
                existing={ai.ragSources?.reference || []}
                onPick={(name) => setAi({ ragSources: { ...(ai.ragSources || { clientAdds: [] }), reference: [...(ai.ragSources?.reference || []), name] } })}
              />
            </div>
          </div>
          <div>
            <div className="cs-inline-label">Client deployment adds <ClassBadge c="C" /></div>
            <ListEditor items={ai.ragSources?.clientAdds || []} onChange={(v) => setAi({ ragSources: { ...(ai.ragSources || { reference: [] }), clientAdds: v } })} placeholder="client source…" />
          </div>
        </div>
      </Sect>

      <Sect title="Prompt Set" specRef="§13.4" hint="Reusable prompts configured for the workspace">
        <div className="cs-factor-list">
          {(ai.promptSet || []).map((p, i) => (
            <span key={i} className="cs-list-item"><span className="cs-list-num">{i + 1}.</span>{p}</span>
          ))}
        </div>
      </Sect>
    </div>
  );
};

// ── Slide-4 menu portlets: the former Setup portlet split by purpose ──
// Approved Clinical Sources (§5) · Client Formulary & Preferences (§7/§9.3)
// · Testing & Validation (§13.5 human attestations / §18.2) · Versioning &
// Approvals (§13.1 profile + §13.5 gate + §7 CCR ledger).

/** §13.5 items derived from the decision resource + masters state; keys are gate item ids. */
export type ComputedGate = Record<string, { status: "met" | "failed" | "na"; detail: string }>;

const SOURCE_STATUS_STYLE: Record<string, React.CSSProperties> = {
  approved: { color: "var(--status-success, #2e7d5b)", fontWeight: 700 },
  draft: { color: "var(--text-muted)" },
  retired: { color: "var(--status-danger, #c53030)" },
};

/** §5 — Approved Clinical Sources portlet: the source hierarchy (moved out of
 *  the former Setup portlet) plus a read view of the Clinical Source master
 *  registry. Registry management (add/retire, documents, approval) stays on
 *  /clinical-domains — a source edit affects every resource that references
 *  it, so it is a masters-level act. */
export const SourcesSpecSections: React.FC<SectionsProps & {
  sourceRegistry?: ClinicalSourceRecord[];
}> = ({ spec, update, sourceRegistry }) => (
  <div className="cs">
    {(spec.clinicalSources || []).length > 0 && (
      <Sect title="Clinical Source & Approval Hierarchy" specRef="§5"
        hint="What each specification is built from — pick primary sources from the domain's Clinical Source registry">
        <div className="drs-matrix-wrap">
          <table className="cs-table cs-table-bordered">
            <thead>
              <tr><th style={{ width: 200 }}>Specification</th><th>Primary sources</th><th>SynerGen configuration</th></tr>
            </thead>
            <tbody>
              {spec.clinicalSources!.map((cs, i) => (
                <tr key={cs.id || i}>
                  <td style={{ fontWeight: 700 }}>{cs.specification}</td>
                  <td>
                    <Form.Control size="sm" value={cs.primarySources}
                      onChange={(e) => update({ clinicalSources: spec.clinicalSources!.map((x, y) => (y === i ? { ...x, primarySources: e.target.value } : x)) })} />
                    <div style={{ marginTop: 4 }}>
                      <RegistryPicker
                        registry={sourceRegistry}
                        existing={[cs.primarySources]}
                        onPick={(name) => update({
                          clinicalSources: spec.clinicalSources!.map((x, y) =>
                            (y === i ? { ...x, primarySources: x.primarySources ? `${x.primarySources} + ${name}` : name } : x)),
                        })}
                      />
                    </div>
                  </td>
                  <td>
                    <Form.Control size="sm" value={cs.synergenConfiguration}
                      onChange={(e) => update({ clinicalSources: spec.clinicalSources!.map((x, y) => (y === i ? { ...x, synergenConfiguration: e.target.value } : x)) })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sect>
    )}

    <Sect title="Source registry" specRef="§5 / §18.1"
      hint="Master records this studio consumes — approval status feeds the §13.5 gate; manage the registry itself in Clinical Domains">
      {(sourceRegistry || []).length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          No sources in the registry (or clinical masters unavailable).
        </p>
      ) : (
        <div className="drs-matrix-wrap">
          <table className="cs-table cs-table-bordered">
            <thead>
              <tr><th>Source</th><th>Type</th><th>Status</th><th>Documents</th><th>RAG</th></tr>
            </thead>
            <tbody>
              {sourceRegistry!.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.name}{s.publisher ? <span style={{ fontWeight: 400, color: "var(--text-faint)" }}> — {s.publisher}{s.year ? ` ${s.year}` : ""}</span> : null}</td>
                  <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.type}</td>
                  <td style={SOURCE_STATUS_STYLE[s.approvalStatus] || undefined}>{s.approvalStatus}</td>
                  <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{(s.resourceIds || []).length || "—"}</td>
                  <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.ragStatus || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <a href="/clinical-domains" style={{ fontSize: 12, fontWeight: 600 }}>Manage registry →</a>
      </div>
    </Sect>
  </div>
);

/** §6 Class C / §7 / §9.3 — Client Formulary & Preferences portlet. Thin on
 *  base templates by design: an explicit "awaiting client configuration"
 *  surface is exactly the §7 story. */
export const FormularySpecSections: React.FC<SectionsProps & {
  medicationClasses?: MasterMedicationClass[];
}> = ({ spec, update, medicationClasses, derived }) => {
  const cf = spec.clientFormulary || {};
  const setCf = (patch: Partial<NonNullable<ClinicalSpec["clientFormulary"]>>) =>
    update({ clientFormulary: { ...cf, ...patch } });
  const rows = cf.preferredAgents || [];
  const cc = spec.clientConfiguration || {};
  const formularyCcr = (cc.unresolved || [])
    .map((u, idx) => ({ u, idx }))
    .filter(({ u }) => /formulary|prescrib|access|preference/i.test(`${u.key} ${u.area}`));
  const setUnresolvedAt = (i: number, value: string) =>
    update({ clientConfiguration: { ...cc, unresolved: (cc.unresolved || []).map((u, y) => (y === i ? { ...u, value } : u)) } });

  const setRow = (i: number, patch: Partial<{ medicationClass: string; classKey?: string; agent?: string; tier?: string; restriction?: string }>) =>
    setCf({ preferredAgents: rows.map((r, y) => (y === i ? { ...r, ...patch } : r)) });

  return (
    <div className="cs">
      <Sect title="Formulary status" specRef="§7 / §9.1"
        hint="The reference matrix carries guideline roles, not a SynerGen formulary — the formulary is a deployment input">
        {formularyCcr.length > 0 ? (
          <table className="cs-table">
            <tbody>
              {formularyCcr.map(({ u, idx }) => (
                <tr key={idx}>
                  <td className="cs-table-key">{u.key} <SpecRef r={u.specRef} /></td>
                  <td style={{ fontSize: 11, color: "var(--text-muted)" }}>{u.area}</td>
                  <td><CcrValue value={u.value} derived={derived} onChange={(v) => setUnresolvedAt(idx, v)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
            No formulary-related deployment fields declared in the client-configuration ledger.
          </p>
        )}
        {spec.medicationMatrix?.notAFormularyNote && <div className="cs-note">{spec.medicationMatrix.notAFormularyNote}</div>}
        <p style={{ fontSize: 11, color: "var(--text-faint)", margin: "8px 0 0" }}>
          This is a filtered view of the deployment-field ledger — add or remove fields in Versioning &amp; Approvals → Client Configuration Required.
          New fields whose name or area mentions formulary, prescribing, access, or preference appear here automatically.
        </p>
      </Sect>

      <Sect title="Preferred agents & tiers" specRef="§9.3"
        hint="Class C deployment content — feeds the formulary / access / preference eligibility factors; leave empty on base templates">
        {rows.length > 0 && (
          <div className="drs-matrix-wrap">
            <table className="cs-table cs-table-bordered">
              <thead>
                <tr><th style={{ width: 190 }}>Medication class</th><th>Preferred agent</th><th style={{ width: 80 }}>Tier</th><th>Restriction</th><th style={{ width: 30 }} /></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      {(medicationClasses || []).length > 0 ? (
                        <Form.Select size="sm" value={r.classKey || ""}
                          onChange={(e) => {
                            const m = (medicationClasses || []).find((x) => x.classKey === e.target.value);
                            setRow(i, { classKey: e.target.value || undefined, medicationClass: m?.name ?? r.medicationClass });
                          }}>
                          <option value="">{r.medicationClass || "— class —"}</option>
                          {medicationClasses!.map((m) => <option key={m.classKey} value={m.classKey}>{m.name}</option>)}
                        </Form.Select>
                      ) : (
                        <Form.Control size="sm" value={r.medicationClass} placeholder="class"
                          onChange={(e) => setRow(i, { medicationClass: e.target.value })} />
                      )}
                    </td>
                    <td><Form.Control size="sm" value={r.agent || ""} placeholder="e.g. lisinopril" onChange={(e) => setRow(i, { agent: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={r.tier || ""} placeholder="1" onChange={(e) => setRow(i, { tier: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={r.restriction || ""} placeholder="e.g. specialist review" onChange={(e) => setRow(i, { restriction: e.target.value })} /></td>
                    <td>
                      <button type="button" className="drs-del" title="Remove row"
                        onClick={() => setCf({ preferredAgents: rows.filter((_, y) => y !== i) })}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: rows.length > 0 ? 8 : 0 }}>
          <Button type="button" size="sm" variant="secondary"
            onClick={() => setCf({ preferredAgents: [...rows, { medicationClass: "" }] })}>
            + Add preferred agent
          </Button>
        </div>
      </Sect>
    </div>
  );
};

/** §13.5 human attestations + §18.2 — Testing & Validation portlet. The gate's
 *  genuinely human checkboxes are EDITED here; the full gate (computed + human,
 *  read-only) is summarized in Versioning & Approvals. */
export const TestingValidationSections: React.FC<SectionsProps & {
  computedGate?: ComputedGate;
  /** Read-only machine-validation lines (workflow validity, safety exits, escalation routes). */
  machineChecks?: Array<{ label: string; status: "met" | "failed" | "na"; detail: string }>;
}> = ({ spec, update, computedGate, machineChecks }) => {
  const gate = spec.publicationGate || {};
  const gateItems = gate.items || [];
  const setGate = (patch: Partial<NonNullable<ClinicalSpec["publicationGate"]>>) =>
    update({ publicationGate: { ...gate, ...patch } });
  const human = gateItems.map((it, i) => ({ it, i })).filter(({ it }) => !computedGate?.[it.id]);

  return (
    <div className="cs">
      <Sect title="Human attestations" specRef="§13.5"
        hint="The publication-gate items no machine can derive — prompt testing, validation scenarios, SME review">
        {human.length === 0 && (
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No human attestation items in the publication gate.</p>
        )}
        <div className="cs-gate">
          {human.map(({ it, i }) => (
            <label key={it.id || i} className={"cs-gate-item" + (it.status === "met" ? " met" : "")}>
              <input
                type="checkbox"
                checked={it.status === "met"}
                onChange={(e) => setGate({ items: gateItems.map((x, y) => (y === i ? { ...x, status: e.target.checked ? "met" : "pending" } : x)) })}
              />
              {it.requirement}
            </label>
          ))}
        </div>
      </Sect>

      {(machineChecks || []).length > 0 && (
        <Sect title="Machine validation" hint="Derived live from the executable workflow — read-only here; the full gate lives in Versioning & Approvals">
          <div className="cs-gate">
            {machineChecks!.map((c, i) => (
              <div key={i}
                className={"cs-gate-item cs-gate-auto" + (c.status === "met" ? " met" : c.status === "na" ? " na" : " failed")}
                title={c.detail}>
                <span className="cs-gate-mark">{c.status === "met" ? "✓" : c.status === "na" ? "—" : "✗"}</span>
                <span>{c.label}<small className="cs-gate-detail">{c.detail}</small></span>
              </div>
            ))}
          </div>
        </Sect>
      )}

      <Sect title="Clinical scenario suites" specRef="§18.2"
        hint="Minimum validation scenarios per pathway, comorbidity, medication and AI-governance — future work; wf-e2e covers workflow/safety/authority mechanics today">
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          Not yet modeled. When built, per-pathway scenario runs will report here and feed the gate.
        </p>
      </Sect>
    </div>
  );
};

// ── Versioning & Approvals portlet (§13.1 profile + §13.5 gate + §7 CCR ledger) ──

export const GovernanceSections: React.FC<SectionsProps & {
  /** §13.5 items derived from the decision resource + masters state; keys are gate item ids. */
  computedGate?: ComputedGate;
}> = ({ spec, update, computedGate, derived }) => {
  const p = spec.profile || {};
  const gate = spec.publicationGate || {};
  const cc = spec.clientConfiguration || {};
  const setProfile = (patch: any) => update({ profile: { ...p, ...patch } });
  const setUnresolvedAt = (i: number, patch: Partial<{ key: string; area: string; value: string }>) =>
    update({ clientConfiguration: { ...cc, unresolved: (cc.unresolved || []).map((u, y) => (y === i ? { ...u, ...patch } : u)) } });
  const removeUnresolvedAt = (i: number) =>
    update({ clientConfiguration: { ...cc, unresolved: (cc.unresolved || []).filter((_, y) => y !== i) } });
  const addUnresolved = () =>
    update({
      clientConfiguration: {
        ...cc,
        unresolved: [...(cc.unresolved || []), { key: "", area: "", value: CLIENT_CONFIGURATION_REQUIRED, custom: true }],
      },
    });

  const gateItems = gate.items || [];
  const effStatus = (it: { id: string; status: string }) => computedGate?.[it.id]?.status ?? it.status;
  const applicable = gateItems.filter((i) => effStatus(i) !== "na");
  const metCount = applicable.filter((i) => effStatus(i) === "met").length;

  const profileField = (key: keyof typeof p, label: string, example?: string, hint?: string) => (
    <div>
      <label className="drs-mini-label">{label}</label>
      <Form.Control
        size="sm"
        value={(p as any)[key] || ""}
        placeholder={example ? `e.g. ${example}` : undefined}
        title={example ? `Example: ${example}` : undefined}
        onChange={(e) => setProfile({ [key]: e.target.value })}
      />
      {hint && <div style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 2, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );

  return (
    <div className="cs">
      <Sect title="Decision Resource Profile" specRef="§13.1 / §13.2">
        <div className="cs-grid-2">
          {profileField("primaryReferenceSME", "Primary Reference SME", "SynerGen Clinical Governance / Pharmacy SME")}
          {profileField("deploymentSME", "Deployment SME", "Client Pharmacy Leader or designated clinical SME")}
          {profileField("primaryUser", "Primary User", "Pharmacist")}
          {profileField("decisionPoint", "Decision Point", "Hypertension Risk Review",
            "The ONE decision in the care journey this resource serves (§13.1). A condition can have several resources, each a different decision point — e.g. \u201cHF GDMT Optimization Review\u201d vs \u201cPost-Discharge Titration Review\u201d.")}
          {profileField("scope", "Scope", "Pharmacist-led adult hypertension decision support")}
          {profileField("population", "Population", "Adults with hypertension")}
        </div>
      </Sect>

      {gateItems.length > 0 && (
        <Sect title={`Publication Gate — ${metCount}/${applicable.length} met`} specRef="§13.5"
          hint="Computed items derive live from the decision resource and the clinical masters; human attestations are made in Testing & Validation">
          <div className="cs-gate">
            {gateItems.map((it, i) => {
              const auto = computedGate?.[it.id];
              if (auto) {
                return (
                  <div key={it.id || i}
                    className={"cs-gate-item cs-gate-auto" + (auto.status === "met" ? " met" : auto.status === "na" ? " na" : " failed")}
                    title={auto.detail}>
                    <span className="cs-gate-mark">{auto.status === "met" ? "✓" : auto.status === "na" ? "—" : "✗"}</span>
                    <span>
                      {it.requirement}
                      <small className="cs-gate-detail">{auto.detail}</small>
                    </span>
                  </div>
                );
              }
              return (
                <div key={it.id || i}
                  className={"cs-gate-item cs-gate-auto" + (it.status === "met" ? " met" : "")}
                  title="Human attestation — check it off in Testing & Validation">
                  <span className="cs-gate-mark">{it.status === "met" ? "✓" : "○"}</span>
                  <span>
                    {it.requirement}
                    <small className="cs-gate-detail">attested in Testing &amp; Validation</small>
                  </span>
                </div>
              );
            })}
          </div>
        </Sect>
      )}

      {(cc.unresolved || []).length > 0 && (
        <Sect title="Client Configuration Required" specRef="§7 / §16.1 / §17.1"
          hint={derived
            ? "This client copy must resolve these — click a badge and enter the client's value (§17.2)"
            : "Deployment-specific fields intentionally unresolved in the SynerGen reference; resolved on client copies"}>
          <table className="cs-table">
            <tbody>
              {cc.unresolved!.map((u, i) => {
                // Seeded reference rows are the base contract: key/area lock on
                // client copies (resolvable, not reshapeable); custom rows stay
                // fully editable and deletable everywhere.
                const editable = u.custom || !derived;
                const deletable = u.custom || !derived;
                return (
                  <tr key={i}>
                    <td className="cs-table-key" style={{ minWidth: 220 }}>
                      {editable ? (
                        <Form.Control size="sm" value={u.key} placeholder="field name"
                          autoFocus={u.custom && u.key === ""}
                          onChange={(e) => setUnresolvedAt(i, { key: e.target.value })} />
                      ) : (
                        u.key
                      )}{" "}
                      <SpecRef r={u.specRef} />
                    </td>
                    <td style={{ fontSize: 11, color: "var(--text-muted)", minWidth: 140 }}>
                      {editable ? (
                        <Form.Control size="sm" value={u.area} placeholder="area (e.g. Integration)" list="cs-ccr-areas"
                          onChange={(e) => setUnresolvedAt(i, { area: e.target.value })} />
                      ) : (
                        u.area
                      )}
                    </td>
                    <td><CcrValue value={u.value} derived={derived} onChange={(v) => setUnresolvedAt(i, { value: v })} /></td>
                    <td style={{ width: 30 }}>
                      {deletable && (
                        <button type="button" className="drs-del" title={u.custom ? "Remove this deployment field" : "Remove from the reference contract (base only)"}
                          onClick={() => removeUnresolvedAt(i)}>×</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="cs-ccr-areas">
            {Array.from(new Set((cc.unresolved || []).map((u) => u.area).filter(Boolean))).map((a) => <option key={a} value={a} />)}
          </datalist>
          <Button type="button" size="sm" variant="secondary" onClick={addUnresolved}>
            + Add deployment field
          </Button>
          {cc.productionRule && <div className="cs-note">🛡 {cc.productionRule}</div>}
        </Sect>
      )}
    </div>
  );
};
